import {
  create_requestor_case,
  fetch_requestor_application_documents,
  fetch_requestor_application_forms,
  fetch_requestor_entity_profiles,
  fetch_requestor_entity_types,
  fetch_requestor_form_details,
  submit_requestor_form_answers,
  upload_requestor_application_documents,
} from '@/services/requestor_portal_service';
import {
  fetch_assigned_questions_to_assessment,
  submit_risk_assessment_answers,
} from '@/services/risk_service';
import {
  getXVerdictFullConfigByEntityType,
  getXVerdictFullConfigById,
  listXVerdictEntityConfigs,
} from '@/services/xverdict_entity_config_service';
import {
  activeFormBindings,
  mapEntityConfigToCaseTypeOption,
  mapRiskBindingsToAssessments,
} from '@/services/xverdict_config_mapper';
import {
  build_case_fact_items,
  document_from_answer,
  first_document_url,
  map_documents,
  split_answer_values,
} from './request_portal_document_helpers';
import {
  first_order_value,
  get_entity_type_name,
  item_identity,
  object_label,
  question_key,
  question_required_value,
  record_domain_label,
  record_status_label,
  section_display_name,
  sorted_items,
  strict_boolean_true,
} from './request_portal_record_helpers';
export { record_status_id } from './request_portal_record_helpers';

const PROJECT_SESSION_KEYS = [
  'project_id',
  'selected_project_id',
  'current_project_id',
  'active_project_id',
];
function normalize_text(value) {
  return String(value || '').trim();
}

function normalize_label(value) {
  return normalize_text(value).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

function is_closed_case_status(status) {
  return normalize_label(status).split(' ').includes('closed');
}

function is_active_case_status(case_value) {
  if (typeof case_value === 'object' && case_value !== null) {
    if (case_value.workflow_state && case_value.workflow_state.is_final !== undefined) {
      return !case_value.workflow_state.is_final;
    }

    if (case_value.is_final_workflow_status !== undefined) {
      return !case_value.is_final_workflow_status;
    }

    return is_active_case_status(case_value.status);
  }

  return !is_closed_case_status(case_value);
}

function case_status_tone(status) {
  const normalized_status = normalize_label(status);
  if (!normalized_status) {
    return '';
  }
  return [
    { tokens: ['declined', 'rejected'], tone: 'is-declined' },
    { tokens: ['withdrawn', 'cancelled'], tone: 'is-withdrawn' },
  ].find((item) => item.tokens.some((token) => normalized_status.includes(token)))?.tone || 'is-approved';
}

function to_number(value) {
  const normalized = Number(value);
  return Number.isFinite(normalized) && normalized > 0 ? normalized : null;
}

function first_truthy(values = []) {
  return values.find((value) => value !== null && value !== undefined && value !== '') ?? null;
}

function nested_record_value(record = {}, path = []) {
  return path.reduce((value, key) => value?.[key], record);
}

function first_record_path(record = {}, paths = []) {
  return paths.map((path) => nested_record_value(record, path)).find(Boolean) || null;
}

function format_date(value) {
  if (!value) {
    return 'Today';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return normalize_text(value);
  }

  return date.toLocaleDateString('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function document_match_key(document = {}) {
  return normalize_text(
    document?.title
    || document?.file_name
    || document?.document_name
    || document?.url
  ).toLowerCase();
}

function is_image_document(document = {}) {
  return ['JPG', 'JPEG', 'PNG', 'GIF', 'WEBP'].includes(normalize_text(document?.extension).toUpperCase());
}

function is_pdf_document(document = {}) {
  return normalize_text(document?.extension).toUpperCase() === 'PDF';
}

function read_session_project_id() {
  if (typeof window === 'undefined') {
    return null;
  }

  for (const key of PROJECT_SESSION_KEYS) {
    const project_id = to_number(window.sessionStorage.getItem(key));
    if (project_id) {
      return project_id;
    }
  }

  return null;
}

function matching_risk_choice(answer_value = '', choices = []) {
  const normalized_answer = normalize_label(answer_value);
  return (Array.isArray(choices) ? choices : []).find((choice) =>
    normalize_label(choice?.option || choice?.choice || choice) === normalized_answer
  );
}

function assigned_risk_questions(payload = {}) {
  const rows = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];

  return rows
    .map((row) => {
      const question = row?.question || row;
      return {
        id: to_number(row?.question_id || row?.question || question?.question_id || question?.id),
        text: normalize_text(question?.text || question?.question_text || question?.label || row?.question_text),
        question_text: normalize_text(question?.question_text || question?.text || question?.label || row?.question_text),
        choices: question?.choices || question?.multi_select_choices || row?.choices || [],
      };
    })
    .filter((question) => question.id);
}

export {
  activeFormBindings,
  assigned_risk_questions,
  build_case_fact_items,
  create_requestor_case,
  document_from_answer,
  document_match_key,
  fetch_assigned_questions_to_assessment,
  fetch_requestor_application_documents,
  fetch_requestor_application_forms,
  fetch_requestor_entity_profiles,
  fetch_requestor_entity_types,
  fetch_requestor_form_details,
  first_document_url,
  first_order_value,
  first_record_path,
  first_truthy,
  format_date,
  getXVerdictFullConfigByEntityType,
  getXVerdictFullConfigById,
  get_entity_type_name,
  case_status_tone,
  is_active_case_status,
  is_closed_case_status,
  is_image_document,
  is_pdf_document,
  item_identity,
  listXVerdictEntityConfigs,
  mapEntityConfigToCaseTypeOption,
  mapRiskBindingsToAssessments,
  map_documents,
  matching_risk_choice,
  normalize_label,
  normalize_text,
  object_label,
  question_key,
  question_required_value,
  read_session_project_id,
  record_domain_label,
  record_status_label,
  section_display_name,
  sorted_items,
  split_answer_values,
  strict_boolean_true,
  submit_requestor_form_answers,
  submit_risk_assessment_answers,
  to_number,
  upload_requestor_application_documents,
};
